import { useState, useMemo } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Settings, Search, Monitor, Cloud, Globe, ArrowLeft } from "lucide-react";

import GeneralSettings from "./Tabs/GeneralSettings";
import AWSSettings from "./Tabs/AWSSettings";
import AzureSettings from "./Tabs/AzureSettings";
import GCPSettings from "./Tabs/GCPSettings";
import SaaSAppsSettings from "./Tabs/SaaSAppsSettings";

import { FaAws, FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";

interface TenantSetting {
    id: string;
    name: string;
    description: string;
    icon: any;
    configured: boolean;
    category: string;
}

const SettingsDetails: TenantSetting[] = [
    {
        id: "general-settings",
        name: "General Settings",
        description:
            "Configure basic tenant settings including email notification details, API tokens and preferences for your enterprise environment",
        icon: Settings,
        configured: true,
        category: "Configuration",
    },
    {
        id: "aws-setup",
        name: "AWS Setup",
        description:
            "Configure Amazon Web Services integration with IAM roles, S3 buckets, and EC2 instance management for cloud infrastructure",
        icon: FaAws,
        configured: false,
        category: "Cloud Platform",
    },
    {
        id: "azure-setup",
        name: "Azure Setup",
        description:
            "Set up Microsoft Azure integration including Active Directory, resource groups, and virtual machine management capabilities",
        icon: VscAzure,
        configured: true,
        category: "Cloud Platform",
    },
    {
        id: "gcp-setup",
        name: "GCP Setup",
        description:
            "Configure Google Cloud Platform services with project management, service accounts, and compute engine integration",
        icon: FaGoogle,
        configured: false,
        category: "Cloud Platform",
    },
    {
        id: "saas-apps",
        name: "SaaS Apps",
        description:
            "Connect and configure SaaS applications like GitLab to discover identities, permissions, and activity signals",
        icon: Globe,
        configured: false,
        category: "SaaS Platform",
    },
];

export default function TenantSettingDetails() {
    const [settings] = useState<TenantSetting[]>(SettingsDetails);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeSetting, setActiveSetting] = useState<string | null>(null);

    const filteredSettings = useMemo(() => {
        if (!searchTerm) return settings;

        const searchLower = searchTerm.toLowerCase();
        return settings.filter(
            (setting) =>
                setting.name.toLowerCase().includes(searchLower) ||
                setting.description.toLowerCase().includes(searchLower) ||
                setting.category.toLowerCase().includes(searchLower)
        );
    }, [settings, searchTerm]);

    const renderActiveSetting = () => {
        switch (activeSetting) {
            case "general-settings":
                return <GeneralSettings onBack={() => setActiveSetting(null)} />;
            case "aws-setup":
                return <AWSSettings onBack={() => setActiveSetting(null)} />;
            case "azure-setup":
                return <AzureSettings onBack={() => setActiveSetting(null)} />;
            case "gcp-setup":
                return <GCPSettings onBack={() => setActiveSetting(null)} />;
            case "saas-apps":
                return <SaaSAppsSettings onBack={() => setActiveSetting(null)} />;
            default:
                return null;
        }
    };

    if (activeSetting) return renderActiveSetting();

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                            Tenant Settings
                        </h1>
                        <p className="text-muted-foreground">
                            Configure system settings and security options
                        </p>
                    </div>
                </div>
                <div className="relative w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                        placeholder="Search settings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="input-search-settings"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredSettings.map((setting) => {
                    const IconComponent = setting.icon;

                    return (
                        <Card
                            key={setting.id}
                            className="border-border/40 hover-elevate transition-all duration-200 cursor-pointer"
                            onClick={() => setActiveSetting(setting.id)}
                            data-testid={`card-setting-${setting.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                        <IconComponent className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    {/**
                                     * TODO - need to add the configure the status by the backend api
                                    <Badge
                                        variant="outline"
                                        className={`text-sm px-3 py-1 ${setting.configured
                                            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                                            : "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-700"
                                            }`}
                                    >
                                        {setting.configured ? "Configured" : "Not Configured"}
                                    </Badge>
                                    */}
                                </div>

                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg leading-tight" data-testid={`title-${setting.id}`}>
                                        {setting.name}
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        {setting.description}
                                    </p>
                                    <Badge variant="secondary" className="text-sm">
                                        {setting.category}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {filteredSettings.length === 0 && (
                <div className="text-center py-12">
                    <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No settings found</h3>
                    <p className="text-muted-foreground">
                        Try adjusting your search terms or browse all available settings.
                    </p>
                </div>
            )}
        </div>
    );
}